import { CommonModule, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  OnInit,
  output,
  signal,
  untracked,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import {
  MatButton,
  MatFabButton,
  MatIconButton,
} from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import {
  jsonDeepClone,
  SiaBaseScriptComponent,
  SiaComponentInfo,
  SiaMenuService,
  SiaRouteData,
  SiaTooltipDirective,
  SiaTranslatePipe,
  SiaWindowSize,
  YesNoDialogComponent,
} from '@sia/shared';
import $ from 'jquery';
import { debounceTime, firstValueFrom, merge, Subscription } from 'rxjs';
import { SiaAction } from '../../interfaces/sia-action';
import { SiaActionFired } from '../../interfaces/sia-action-fired';
import { SiaActionsService } from '../../services/sia-actions.service';
import { SiaActionBottomSheetComponent } from '../sia-action-bottom-sheet/sia-action-bottom-sheet.component';

const TYPEID_TO_CONFIG_TYPE: Record<string, string> = {
  'sia-grid': 'grid',
  'pivot-grid': 'pivot',
  'dash-single-page': 'dash',
};

@Component({
  selector: 'sia-actions-container',
  templateUrl: './sia-actions-container.component.html',
  styleUrls: ['./sia-actions-container.component.scss'],
  standalone: true,
  imports: [
    MatButton,
    MatIconButton,
    MatFabButton,
    MatIcon,
    MatMenuModule,
    MatBadgeModule,
    SiaTranslatePipe,
    CommonModule,
    NgTemplateOutlet,
    SiaTooltipDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiaActionsContainerComponent
  extends SiaBaseScriptComponent
  implements AfterViewInit, OnInit
{
  #siaActionsService = inject(SiaActionsService);
  #siaMenuService = inject(SiaMenuService);
  #matBottomSheet = inject(MatBottomSheet);
  #hostEl = inject(ElementRef);
  #ngZone = inject(NgZone);
  cdr = inject(ChangeDetectorRef);

  #actionDebounceMap: Map<string, number> = new Map();
  #resizeObserver?: ResizeObserver;
  #parentResizeObserver?: ResizeObserver;
  #recalcTimeout: any;
  #filterActionsResolve: (() => void) | null = null;
  #widths = new Map<string, number>();
  #overflowBtnWidth = 40;
  #measureRaf: number | null = null;
  #allActions: SiaAction[] = [];
  #subs: Subscription[] = [];

  @ViewChild('measureContainer') measureContainerEl?: ElementRef<HTMLElement>;
  @ViewChild('desktopContainer') desktopContainerEl!: ElementRef;
  @ViewChild('actionContainerMobile') mobileContainerEl?: ElementRef;
  @ViewChild('adminContextTrigger') adminContextTrigger!: MatMenuTrigger;

  componentInfo = signal<SiaComponentInfo | null>(null);
  hasCustomerConfig = signal<boolean>(false);
  isDelta = signal<boolean>(false);
  adminContextMenuX = 0;
  adminContextMenuY = 0;

  // Actions rendered in the hidden measurer (all inline)
  allInlineActionsForMeasure = computed(() => [
    ...this.#staticVisibleActions(),
    ...this.#nonStaticVisibleActions(),
  ]);

  #requestMeasureAndRecalc() {
    if (this.mode() !== 'desktop') return;

    if (this.#measureRaf != null) cancelAnimationFrame(this.#measureRaf);

    this.#measureRaf = requestAnimationFrame(() => {
      this.#measureWidthsFromHiddenToolbar();
      this.#recalcCompactingIncremental();
    });
  }

  #measureWidthsFromHiddenToolbar() {
    const el = this.measureContainerEl?.nativeElement;
    if (!el) return;

    // action wrappers
    const items = Array.from(
      el.querySelectorAll<HTMLElement>('[data-measure-key]'),
    );
    for (const node of items) {
      const key = node.dataset['measureKey'];
      if (!key) continue;
      const w = node.offsetWidth;
      if (w > 0) this.#widths.set(key, w);
    }

    // overflow trigger
    const overflow = el.querySelector<HTMLElement>(
      '[data-measure-overflow="true"]',
    );
    if (overflow?.offsetWidth) this.#overflowBtnWidth = overflow.offsetWidth;
  }

  #getW(key: string, fallback = 48) {
    return this.#widths.get(key) ?? fallback;
  }

  #fits(compactedCount: number, availableWidth: number) {
    const gap = 8;
    const safetyBuffer = 32;

    const staticActions = this.#staticVisibleActions();
    const nonStatic = this.#nonStaticVisibleActions();

    const c = Math.max(0, Math.min(compactedCount, nonStatic.length));
    const inlineNonStatic = nonStatic.slice(c);
    const hasOverflow = c > 0;

    const keys: string[] = [
      ...staticActions
        .map((a) => a.id)
        .filter((id): id is string => id != null),
      ...inlineNonStatic
        .map((a) => a.id)
        .filter((id): id is string => id != null),
    ];

    let sum = 0;
    for (const k of keys) sum += this.#getW(k);

    let itemsCount = keys.length;
    if (hasOverflow) {
      sum += this.#overflowBtnWidth;
      itemsCount += 1;
    }

    sum += Math.max(0, itemsCount - 1) * gap;

    sum += safetyBuffer;

    return sum <= availableWidth;
  }

  #recalcCompactingIncremental() {
    const availableWidth = this.#containerWidth();

    const nonStaticLen = this.#nonStaticVisibleActions().length;
    if (nonStaticLen === 0) {
      this.compactedActionCount.set(0);
      this.minWidth.emit(0);
      return;
    }

    // When no space is available, compact everything and request the minimum
    // width needed for the overflow button. This starts the feedback loop:
    // headerbar receives minWidth → raises availableActionsWidth → compaction
    // can then partially relax on the next recalc cycle.
    if (availableWidth <= 0) {
      if (this.compactedActionCount() !== nonStaticLen) {
        this.compactedActionCount.set(nonStaticLen);
      }
      this.minWidth.emit(Math.ceil(this.#overflowBtnWidth));
      return;
    }

    let c = Math.max(0, Math.min(this.compactedActionCount(), nonStaticLen));

    const maxThreshold =
      this.maxAllowedWidth() != null ? this.maxAllowedWidth()! : availableWidth;

    if (!this.#fits(c, availableWidth)) {
      while (c < nonStaticLen && !this.#fits(c, availableWidth)) {
        c++;
      }
    } else {
      while (
        c > 0 &&
        this.#fits(c - 1, availableWidth) &&
        this.#fits(c - 1, maxThreshold)
      ) {
        c--;
      }
    }

    if (c !== this.compactedActionCount()) {
      this.compactedActionCount.set(c);
    }

    this.minWidth.emit(
      c === nonStaticLen ? Math.ceil(Math.max(0, this.#overflowBtnWidth)) : 0,
    );
  }

  mode = input<'desktop' | 'mobile'>('desktop');
  compact = input<boolean>(false);
  onlyIcons = input<boolean>(false);
  buttonsSize = input<'big' | 'small' | 'medium'>('big');
  debounceDelay = input<number>(300);
  parentWidth = input<number | null>(null);
  componentParameters = input<string>('');
  showBackAction = input<boolean>(true);
  maxAllowedWidth = input<number | null>(null);

  pageData = signal<SiaRouteData>({});
  pageDataInput = input<SiaRouteData | undefined>(undefined, {
    alias: 'pageData',
  });

  isAdmin = signal<boolean>(false);

  actionVisibility = signal<Record<string, boolean>>({});
  disabledActions = signal<Record<string, boolean>>({});
  badgeActions = signal<Record<string, boolean>>({});
  templateActions = signal<Record<string, any>>({});

  #validScriptActions = signal<SiaAction[]>([]);

  #mappedActions = computed(() => {
    const av = this.actionVisibility();
    const da = this.disabledActions();
    const ba = this.badgeActions();
    const ta = this.templateActions();

    return this.#validScriptActions()
      .filter((x) => av[x.id!] !== false)
      .map((action) => ({
        ...action,
        disabled: da[action.id!] === true || da['all'] === true,
        showBadge: ba[action.id!] === true,
        template: ta[action.id!],
      }));
  });

  #allValidNonDisabledActions = computed(() =>
    this.#mappedActions().filter((x) => !x.disabled),
  );
  #userActions = computed(() =>
    this.#mappedActions().filter((x) => !x.adminScope && !x.disabled),
  );

  adminActions = computed(() =>
    this.#mappedActions().filter((x) => x.adminScope && !x.disabled),
  );

  visibleActions = computed(() =>
    this.#userActions().filter((x) => !x.actionGroupAlwaysGrouped),
  );

  staticActions = computed(() => this.#userActions().filter((x) => x.isStatic));
  nonStaticActions = computed(() =>
    this.#userActions().filter((x) => !x.isStatic),
  );

  groups = computed(() => {
    const groupsList = this.#userActions()
      .filter((x) => x.actionGroupId)
      .map((x) => ({
        id: x.actionGroupId!,
        name: x.actionGroupName!,
        icon: x.actionGroupIcon!,
        alwaysGrouped: x.actionGroupAlwaysGrouped,
      }));
    return groupsList.filter(
      (v, i, a) => a.findIndex((t) => t.id === v.id) === i,
    );
  });

  showGenericActionGroup = computed(() =>
    this.#userActions().some((x) => !x.actionGroupId && !x.isStatic),
  );

  groupsActions = computed<Record<string, SiaAction[]>>(() => {
    const actions = this.#userActions();
    const ga: any = {};
    this.groups().forEach((group) => {
      ga[group.id] = actions.filter((x) => x.actionGroupId === group.id);
    });
    ga['generic'] = actions.filter((x) => !x.actionGroupId && !x.isStatic);
    return ga;
  });

  compactedActionCount = signal<number>(0);
  #containerWidth = signal<number>(0);
  minWidth = output<number>();

  #staticVisibleActions = computed(() =>
    this.visibleActions().filter((x) => x.isStatic),
  );
  #nonStaticVisibleActions = computed(() =>
    this.visibleActions().filter((x) => !x.isStatic),
  );

  inlineActions = computed(() => {
    const staticActions = this.#staticVisibleActions();
    const nonStatic = this.#nonStaticVisibleActions();
    const count = this.compactedActionCount();
    const inlineNonStatic = count <= 0 ? nonStatic : nonStatic.slice(count);
    return [...staticActions, ...inlineNonStatic];
  });

  overflowActions = computed(() => {
    const nonStatic = this.#nonStaticVisibleActions();
    const count = this.compactedActionCount();
    if (count <= 0) return [];
    const safeCount = Math.min(count, nonStatic.length);
    // take first actions when compacting from the left
    return nonStatic.slice(0, safeCount);
  });

  leftActions = computed(() => {
    const currentMode = this.pageData().mode;

    if (this.mode() !== 'mobile') return [];
    const value = this.staticActions();
    const middleIndex = Math.ceil(
      (value.length + (currentMode === 'read' ? 1 : 0)) / 2,
    );
    const left = value.slice(0, middleIndex);
    const placeholder: any = this.#createPlaceholder();
    if (left.length === 0) left.push(placeholder);
    return left;
  });

  rightActions = computed(() => {
    const currentMode = this.pageData().mode;

    if (this.mode() !== 'mobile') return [];
    const value = this.staticActions();
    const middleIndex = Math.ceil(
      (value.length + (currentMode === 'read' ? 1 : 0)) / 2,
    );
    const right = value.slice(middleIndex, value.length);
    const left = this.leftActions();

    if (!this.showBackAction() && left.length > right.length) {
      const difference = left.length - right.length;
      const newRight = [...right];
      const placeholder: any = this.#createPlaceholder();
      for (let i = 0; i < difference; i++) newRight.push(placeholder);
      return newRight;
    }
    return right;
  });

  bottomSheetActions = computed(() => {
    if (this.mode() !== 'mobile') return [];
    const actions = this.nonStaticActions().filter((x) => !x.actionGroupId);

    if (this.adminActions().length > 0) {
      actions.push({
        id: 'admin-actions',
        name: 'Azioni amministrative',
        icon: 'settings',
        actionType: 'group',
        disabled: false,
        showBadge: false,
        template: null,
      });
    }
    this.groups().forEach((group) => {
      actions.push({
        id: group.id,
        name: group.name,
        icon: group.icon,
        actionType: 'group',
        disabled: false,
        showBadge: false,
        template: null,
      });
    });
    return actions;
  });

  ngOnDestroy() {
    this.#subs.forEach((sub) => sub.unsubscribe());

    if (this.#filterActionsResolve) {
      this.#filterActionsResolve();
      this.#filterActionsResolve = null;
    }

    clearTimeout(this.#recalcTimeout);
    this.#actionDebounceMap.clear();
    this.#resizeObserver?.disconnect();
    this.#parentResizeObserver?.disconnect();
    this.#subs = [];
    super.ngOnDestroy();
  }

  constructor() {
    super();

    effect(
      () => {
        const pd = this.pageDataInput();
        if (!pd) return;

        untracked(() => {
          const oldPd = this.pageData();
          this.cleanUp();
          const sameComponent =
            pd.componentId === oldPd.componentId && pd.mode === oldPd.mode;

          this.pageData.set(pd || {});
          this.baseRouterPageData = pd || {};

          this.#updateBooleanDict(
            this.siaBaseActionService.visibilityActions,
            this.actionVisibility,
          );
          this.#updateBooleanDict(
            this.siaBaseActionService.disabledActions,
            this.disabledActions,
          );
          this.#updateBooleanDict(
            this.siaBaseActionService.actionBadges,
            this.badgeActions,
          );
          this.#updateTemplateDict(this.siaBaseActionService.templateActions);

          this.updateActions(sameComponent);
          this.#checkCustomerConfig();
        });
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const pw = this.parentWidth();
        // Update whenever parentWidth is explicitly provided (even as 0),
        // so recalculation fires and can compact/emit minWidth correctly.
        if (pw != null) {
          this.#containerWidth.set(pw);
        }
      },
      { allowSignalWrites: true },
    );

    effect(() => {
      this.#containerWidth();
      this.visibleActions();
      this.groups();
      this.adminActions();

      untracked(() => {
        // Schedule via new hidden-toolbar flow
        this.#requestMeasureAndRecalc();
      });
    });
  }
  cleanUp() {
    this.#validScriptActions.set([]);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.isAdmin.set(this.siaUsersService.getUserData()?.isAdmin ?? false);

    const refresh$ = merge(
      this.#siaActionsService.actionParamsChanged$(),
      this.siaParamsAndPatternsService.paramsHardUpdated$,
      this.siaParamsAndPatternsService.departmentChanged$,
    );

    this.#subs.push(
      refresh$.subscribe(async () => {
        await this.#filterActions(this.#allActions);
      }),
    );

    this.#subs.push(
      this.siaSizeDetector.onResize$
        .pipe(debounceTime(80))
        .subscribe((size: SiaWindowSize | null) => {
          if (!size) return;
          this.#updateContainerWidth();
        }),
    );

    const s3 = this.siaBaseActionService.visibilityActionsSubject.subscribe(
      () =>
        this.#updateBooleanDict(
          this.siaBaseActionService.visibilityActions,
          this.actionVisibility,
        ),
    );
    this.#subs.push(s3);

    const s4 = this.siaBaseActionService.disabledActionsSubject.subscribe(() =>
      this.#updateBooleanDict(
        this.siaBaseActionService.disabledActions,
        this.disabledActions,
      ),
    );
    this.#subs.push(s4);

    const s5 = this.siaBaseActionService.badgeActionsSubject.subscribe(() =>
      this.#updateBooleanDict(
        this.siaBaseActionService.actionBadges,
        this.badgeActions,
      ),
    );
    this.#subs.push(s5);

    const s6 = this.siaBaseActionService.templateActionsSubject.subscribe(() =>
      this.#updateTemplateDict(this.siaBaseActionService.templateActions),
    );
    this.#subs.push(s6);

    const s7 = this.#siaActionsService
      .fireActionIfVisible$()
      .subscribe((data: any) => {
        const action = this.#allValidNonDisabledActions().find(
          (x) => x.id === data.actionId,
        );
        if (!action) return;
        this.fireAction(action);
      });
    this.#subs.push(s7);
  }

  ngAfterViewInit() {
    if (this.mode() === 'desktop') {
      this.#setupResizeObservers();
    }
  }

  #setupResizeObservers() {
    this.#resizeObserver = new ResizeObserver(() => {
      this.#ngZone.run(() => this.#updateContainerWidth());
    });

    const parentEl = this.#hostEl.nativeElement.parentElement;
    if (parentEl) {
      this.#parentResizeObserver = new ResizeObserver(() => {
        this.#ngZone.run(() => this.#updateContainerWidth());
      });
      this.#parentResizeObserver.observe(parentEl);
    }

    if (this.#hostEl.nativeElement) {
      this.#resizeObserver.observe(this.#hostEl.nativeElement);
    }
    this.#updateContainerWidth();
  }

  #updateContainerWidth() {
    const pw = this.parentWidth();
    if (pw != null) {
      // parentWidth is explicitly managed by the headerbar — just sync it.
      // Do NOT emit minWidth here; only recalcCompactingIncremental owns that signal.
      this.#containerWidth.set(pw);
    } else {
      this.#containerWidth.set(this.#hostEl.nativeElement.offsetWidth);
    }
  }

  #getComponentDict(source: any): Record<string, any> {
    const pd = this.pageData();
    if (pd.componentId && pd.guid) {
      return (source[pd.guid] ?? {})[pd.componentId!] ?? {};
    }
    return {};
  }

  #updateBooleanDict(source: any, signal: WritableSignal<any>) {
    let dict = this.#getComponentDict(source);
    if (JSON.stringify(dict) != JSON.stringify(signal())) {
      signal.set(jsonDeepClone(dict));
    }
  }

  #updateTemplateDict(source: any) {
    this.templateActions.set(this.#getComponentDict(source));
  }

  #createPlaceholder() {
    return { disabled: false, showBadge: false, template: null };
  }

  async updateActions(skipServerLoad = false) {
    this.cdr.detach();
    try {
      const pd = this.pageData();
      if (!pd.componentId || !pd.mode) return;

      if (!skipServerLoad) {
        const serverActions =
          await this.#siaActionsService.getActionsByComponent(
            pd.componentId,
            pd.mode,
          );
        this.#allActions = jsonDeepClone(serverActions);
      }
      if (!this.#allActions || !this.#allActions.length) {
        await this.#filterActions([]);
        return;
      }

      await this.#filterActions(this.#allActions);
    } finally {
      this.cdr.reattach();
    }
  }

  #filterActions(fullActions: SiaAction[]): Promise<void> {
    if (this.#filterActionsResolve) {
      this.#filterActionsResolve();
    }

    return new Promise<void>((resolve) => {
      this.#filterActionsResolve = resolve;

      Promise.resolve().then(async () => {
        const actions: SiaAction[] = jsonDeepClone(fullActions);
        const parameters = this.siaSessionService.getSessionParams(
          this.pageData().guid,
        );

        const evalPromises = actions.map(async (action) => {
          if (action.visibilityScript) {
            const isOk = await this.execScript(
              action.visibilityScript,
              parameters,
            );
            return { action, isOk };
          }
          return { action, isOk: true };
        });

        const evaluated = await Promise.all(evalPromises);
        const validScriptActions = evaluated
          .filter((r) => r.isOk)
          .map((r) => r.action);

        this.#validScriptActions.set(validScriptActions);

        this.minWidth.emit(0);

        this.compactedActionCount.update((c) =>
          Math.min(c, this.#nonStaticVisibleActions().length),
        );
        this.#requestMeasureAndRecalc();

        if (this.#filterActionsResolve === resolve) {
          resolve();
          this.#filterActionsResolve = null;
        }
      });
    });
  }

  async fireAction(action: SiaAction, actionTrigger?: MatMenuTrigger) {
    const pd = this.pageData();
    const actionKey = `${pd.componentId ?? ''}_${action.id}`;
    const now = Date.now();
    const lastExecuted = this.#actionDebounceMap.get(actionKey) || 0;

    if (now - lastExecuted < this.debounceDelay()) return;
    this.#actionDebounceMap.set(actionKey, now);

    if (action.script) {
      await this.execScript(action.script);
      return;
    }
    const actionElement = $('#' + (pd.componentId ?? '') + action.id)[0];
    if (!action.dialogEnabled) {
      const act: SiaActionFired = {
        action: action,
        guid: pd.guid,
        componentId: pd.componentId,
        htmlElement: actionElement,
        actionTrigger: actionTrigger,
      };
      this.#matBottomSheet.dismiss();
      this.#siaActionsService.fireSiaAction(act);
      return;
    } else {
      this.openActionDialog(action);
    }
  }

  async openActionDialog(action: SiaAction) {
    const dialogRef = this.dialog.open(YesNoDialogComponent, {
      width: '350px',
      data: {
        title: action.dialogMessage,
        negativeMeaning: action.dialogNegativeMeaning,
      },
    });
    const s0 = dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const act: SiaActionFired = {
          action: action,
          guid: this.pageData().guid,
          componentId: this.pageData().componentId,
        };
        this.#siaActionsService.fireSiaAction(act);
      }
    });
    this.#subs.push(s0);
  }

  async onAdminContextMenu(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    const pd = this.pageData();
    if (!pd.componentId) return;

    this.adminContextMenuX = event.clientX;
    this.adminContextMenuY = event.clientY;

    const info = await this.#siaMenuService.getComponentInformation(
      pd.componentId,
    );
    this.componentInfo.set(info ?? {});
    this.adminContextTrigger.openMenu();
  }

  async #checkCustomerConfig() {
    const pd = this.pageData();
    if (!pd.componentId) return;

    let info = this.componentInfo();
    if (!info) {
      info = await this.#siaMenuService.getComponentInformation(pd.componentId);
      this.componentInfo.set(info ?? {});
    }

    const typeId = info?.typeId;
    if (!typeId) return;

    const configType = TYPEID_TO_CONFIG_TYPE[typeId] ?? typeId;
    const defaultPerspective =
      await this.componentConfigService.getDefaultPerspective(
        configType,
        pd.componentId,
      );
    this.hasCustomerConfig.set(defaultPerspective?.levelType === 'customer');
    this.isDelta.set(defaultPerspective?.isDelta ?? false);
  }

  navigateToConfigurator() {
    const pd = this.pageData();
    this.siaRouterService.navigateMenuWithParams('close^page-configure', [
      { id: 'componentId', value: pd.componentId },
    ]);
  }

  navigateBackAction() {
    const backOutlet = this.pageData()?.outlet?.replace('tabs', '');
    if (backOutlet?.includes('drawer') || backOutlet?.includes('popup')) {
      this.siaRouterService.navigateBackSecondaryOutlet(backOutlet);
    } else {
      this.siaRouterService.navigateBack();
    }
  }

  async openActionSheet(actions?: SiaAction[]) {
    const actionBottomSheet = this.#matBottomSheet.open(
      SiaActionBottomSheetComponent,
      {
        data: {
          actions: actions ?? this.bottomSheetActions(),
          pageData: this.pageData(),
          componentParameters: this.componentParameters(),
        },
      },
    );

    const actionFired = await firstValueFrom(
      actionBottomSheet.afterDismissed(),
    );
    if (!actionFired) return;

    if (actionFired.actionType === 'group') {
      if (actionFired.id === 'admin-actions') {
        this.openActionSheet(this.adminActions());
      } else {
        this.openActionSheet(this.groupsActions()[actionFired.id]);
      }
    } else {
      this.fireAction(actionFired);
    }
  }
}
